import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormDialog } from '@/components/shared/form-dialog';
import { FormField } from '@/components/shared/form-field';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { resolveApiError } from '@/lib/api-errors';

export const RequestSupportSessionDialog = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { t } = useTranslation('support');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');

  const create = useMutation({
    mutationFn: () => api.post('/support/session-requests', { reason: reason.trim() }),
    onSuccess: async () => {
      toast.success(t('session.requested'));
      setReason('');
      onOpenChange(false);
      await queryClient.invalidateQueries({ queryKey: ['support', 'session-requests'] });
    },
    onError: (err) => toast.error(resolveApiError(err, tErrors)),
  });

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('session.requestTitle')}
      description={t('session.requestDescription')}
      size="md"
      onSubmit={() => create.mutate()}
      submitLabel={t('session.request')}
      cancelLabel={tCommon('action.cancel')}
      submitting={create.isPending}
      submitDisabled={reason.trim().length < 3}
    >
      <FormField label={t('session.reason')} required>
        {(field) => (
          <Textarea
            {...field}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            rows={4}
            placeholder={t('session.reasonPlaceholder')}
          />
        )}
      </FormField>
    </FormDialog>
  );
};
